// Reine Datumslogik für „Verschieben“ (Inbox und Aufgaben): Schnellwahlen wie in Close (Morgen, die nächsten
// Wochentage, nächster Montag, 2 Wochen, 1 Monat, 3 Monate) und die Wochen eines Monatskalenders.
// Bewusst ohne Importe, damit Node die Datei direkt testen kann (components/inbox/tests/schnellwahl.test.mjs).
// Alle Tage sind YYYY-MM-DD (Kalendertage in Berlin, die Zeitzone spielt hier keine Rolle).

const zwei = (n: number) => String(n).padStart(2, "0");

export const WOCHENTAG_LANG = ["Sonntag", "Montag", "Dienstag", "Mittwoch", "Donnerstag", "Freitag", "Samstag"];
export const WOCHENTAG_KURZ = ["So", "Mo", "Di", "Mi", "Do", "Fr", "Sa"];
export const MONAT_NAMEN = ["Januar", "Februar", "März", "April", "Mai", "Juni", "Juli", "August", "September", "Oktober", "November", "Dezember"];

function teile(tag: string): [number, number, number] {
  const [j, m, t] = tag.slice(0, 10).split("-").map(Number);
  return [j, m, t];
}

function ausDatum(d: Date): string {
  return `${d.getUTCFullYear()}-${zwei(d.getUTCMonth() + 1)}-${zwei(d.getUTCDate())}`;
}

export function tagPlus(tag: string, n: number): string {
  const [j, m, t] = teile(tag);
  return ausDatum(new Date(Date.UTC(j, m - 1, t + n)));
}

/** 0 = Sonntag … 6 = Samstag */
export function wochentag(tag: string): number {
  const [j, m, t] = teile(tag);
  return new Date(Date.UTC(j, m - 1, t)).getUTCDay();
}

/** Monate addieren; der Tag wird auf den letzten des Zielmonats gekürzt (31.01. + 1 Monat = 28.02.). */
export function monatPlus(tag: string, n: number): string {
  const [j, m, t] = teile(tag);
  const ziel = new Date(Date.UTC(j, m - 1 + n, 1));
  const letzter = new Date(Date.UTC(ziel.getUTCFullYear(), ziel.getUTCMonth() + 1, 0)).getUTCDate();
  return `${ziel.getUTCFullYear()}-${zwei(ziel.getUTCMonth() + 1)}-${zwei(Math.min(t, letzter))}`;
}

/** „Mi 14.10.“ */
export function kurzDatum(tag: string): string {
  const [, m, t] = teile(tag);
  return `${WOCHENTAG_KURZ[wochentag(tag)]} ${zwei(t)}.${zwei(m)}.`;
}

export interface Schnellwahl {
  label: string;
  tag: string;
}

/**
 * Schnellwahlen ab `heute`: Morgen, die nächsten zwei Werktage danach mit Namen („Donnerstag“, „Freitag“; das
 * Wochenende wird übersprungen), der nächste Montag (wenn er nicht schon dabei ist), in 2 Wochen, in 1 Monat, in 3 Monaten.
 * Ist die Aufgabe nicht schon für heute, steht „Heute“ (später am Tag, mit Uhrzeit) vorn.
 */
export function schnellwahlen(heute: string, aktuellerTag?: string | null): Schnellwahl[] {
  const liste: Schnellwahl[] = [];
  if (aktuellerTag?.slice(0, 10) !== heute) liste.push({ label: "Heute", tag: heute });
  liste.push({ label: "Morgen", tag: tagPlus(heute, 1) });
  let tag = tagPlus(heute, 2);
  let werktage = 0;
  for (let i = 0; i < 8 && werktage < 2; i++, tag = tagPlus(tag, 1)) {
    const w = wochentag(tag);
    if (w === 0 || w === 6) continue;
    liste.push({ label: WOCHENTAG_LANG[w], tag });
    werktage += 1;
  }
  // nächster Montag nach heute
  const w = wochentag(heute);
  const montag = tagPlus(heute, w === 1 ? 7 : (8 - w) % 7 || 7);
  if (!liste.some((s) => s.tag === montag)) liste.push({ label: "Nächster Montag", tag: montag });
  liste.push({ label: "In 2 Wochen", tag: tagPlus(heute, 14) });
  liste.push({ label: "In 1 Monat", tag: monatPlus(heute, 1) });
  liste.push({ label: "In 3 Monaten", tag: monatPlus(heute, 3) });
  return liste;
}

export interface Vorschlag {
  label: string;
  tag: string;
  kurz: string;
}

/** Vorschläge für das „Follow-UP Datum“ ab `heute`: Morgen, Übermorgen, Nächste Woche (nächster Montag), In 2 Wochen. */
export function followUpVorschlaege(heute: string): Vorschlag[] {
  const w = wochentag(heute);
  const montag = tagPlus(heute, w === 1 ? 7 : (8 - w) % 7 || 7);
  const liste: [string, string][] = [
    ["Morgen", tagPlus(heute, 1)],
    ["Übermorgen", tagPlus(heute, 2)],
    ["Nächste Woche", montag],
    ["In 2 Wochen", tagPlus(heute, 14)],
  ];
  // fällt „nächste Woche“ auf einen schon genannten Tag (Sonntag: Montag = morgen), entfällt der Doppelgänger
  return liste.filter(([, tag], i) => liste.findIndex(([, t]) => t === tag) === i).map(([label, tag]) => ({ label, tag, kurz: kurzDatum(tag) }));
}

export interface KalenderTag {
  tag: string;
  imMonat: boolean;
}

/** Wochen (Montag bis Sonntag) eines Monats `YYYY-MM`, aufgefüllt mit Tagen der Nachbarmonate. */
export function kalenderWochen(monat: string): KalenderTag[][] {
  const [j, m] = monat.split("-").map(Number);
  const erster = `${j}-${zwei(m)}-01`;
  const versatz = (wochentag(erster) + 6) % 7; // Montag = 0
  const start = tagPlus(erster, -versatz);
  const wochen: KalenderTag[][] = [];
  for (let w = 0; w < 6; w++) {
    const woche: KalenderTag[] = [];
    for (let d = 0; d < 7; d++) {
      const tag = tagPlus(start, w * 7 + d);
      woche.push({ tag, imMonat: tag.slice(0, 7) === `${j}-${zwei(m)}` });
    }
    if (w >= 4 && !woche.some((x) => x.imMonat)) break; // keine leere sechste Woche
    wochen.push(woche);
  }
  return wochen;
}

/** Monat `YYYY-MM` um n Monate verschieben. */
export function monatVerschieben(monat: string, n: number): string {
  const [j, m] = monat.split("-").map(Number);
  const d = new Date(Date.UTC(j, m - 1 + n, 1));
  return `${d.getUTCFullYear()}-${zwei(d.getUTCMonth() + 1)}`;
}

/** „Oktober 2026“ */
export function monatName(monat: string): string {
  const [j, m] = monat.split("-").map(Number);
  return `${MONAT_NAMEN[m - 1]} ${j}`;
}
